import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Trophy, CalendarCheck, ChevronRight, Clock, Users2,
  Dumbbell, ClipboardList, ArrowRight, Activity, Zap, CalendarDays, Flame, MapPin,
} from "lucide-react";
import { StatusPill, AttendanceRegisterEditor } from "@/components/AttendanceQuickPickerDialog";
import WeaponSummary from "@/components/WeaponSummary";
import { useAuth } from "@/context/AuthContext";
import { DashboardSkeleton } from "@/components/Skeleton";
import { displayName, initials, sortAthletes, matchesSearch } from "@/lib/athleteName";

function extractTime(dateStr) {
  if (!dateStr) return "";
  const m = String(dateStr).match(/T?(\d{2}:\d{2})/);
  return m ? m[1] : "";
}

// Parse YYYY-MM-DD as a LOCAL date (no UTC shift) — same principle used in Training.jsx
function toLocalDate(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return new Date(str);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function daysUntil(str) {
  const d = toLocalDate(str);
  if (!d) return null;
  const now = new Date();
  const t0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((d.getTime() - t0.getTime()) / 86400000);
}

function fmtDate(str) {
  const d = toLocalDate(str);
  if (!d) return str;
  return d.toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" });
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Buongiorno";
  if (h < 19) return "Buon pomeriggio";
  return "Buonasera";
}

const IntensityBadge = ({ intensity }) => {
  if (!intensity) return null;
  const map = {
    bassa: "bg-emerald-50 text-emerald-700 border-emerald-200",
    media: "bg-amber-50 text-amber-700 border-amber-200",
    alta: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${map[intensity] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>
      {intensity}
    </span>
  );
};

const CardShell = ({ title, icon: Icon, iconClass, count, to, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-zinc-200 rounded-md flex flex-col">
    <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
      {Icon && <Icon className={`w-4 h-4 ${iconClass || "text-zinc-500"}`} />}
      <h2 className="font-heading font-semibold tracking-tight">{title}</h2>
      {typeof count === "number" && <span className="ml-auto text-xs font-mono font-semibold text-zinc-700 bg-zinc-100 rounded-full px-2 py-0.5">{count}</span>}
      {to && (
        <Link to={to} className="ml-auto text-[#0052FF]" data-testid={`${testid}-link`}>
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
    {children}
  </section>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [today, setToday] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [editRegisterId, setEditRegisterId] = useState(null);

  const load = () => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({ upcoming_competitions: [], attention: [], week: {}, groups_overview: {}, tests_summary: {} }));
    api.get("/attendance/today").then((r) => setToday(r.data || [])).catch(() => setToday([]));
    api.get("/attendance/alerts?days=30").then((r) => setAlerts(r.data || [])).catch(() => setAlerts([]));
  };

  useEffect(() => { load(); }, []);

  const openRegister = async (trainingId) => {
    try {
      const t = today.find((x) => x.training_id === trainingId);
      let rid = t?.register?.id;
      if (!rid) {
        const { data } = await api.post("/attendance", { training_id: trainingId });
        rid = data.id;
      }
      setEditRegisterId(rid);
    } catch (_) { /* ignore */ }
  };

  const siblings = useMemo(() => today.map((t) => ({
    training_id: t.training_id,
    register_id: t.register?.id || null,
    title: t.title || t.group_name || "Allenamento",
    time: extractTime(t.date),
    status: t.status,
    weapons: t.weapons || [],
  })), [today]);

  if (!data) return <DashboardSkeleton />;

  const upcoming = (data.upcoming_competitions || []).slice(0, 3);
  const attention = sortAthletes(data.attention || []);
  const week = data.week || {};
  const go = data.groups_overview || {};
  const tests = data.tests_summary || {};
  const firstName = (user?.name || "").split(" ")[0] || "";
  const todoToday = today.filter((t) => t.status !== "completato").length;

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-wider text-zinc-500">
          {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
          {greeting()}{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-zinc-500 mt-1">Control room tecnico-operativa del Club.</p>
      </header>

      {/* 1 · OGGI */}
      <CardShell title="Oggi" icon={CalendarCheck} iconClass="text-[#0052FF]" count={today.length} testid="dash-today">
        {today.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-zinc-500" data-testid="dash-today-empty">
            Nessun allenamento programmato per oggi.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {today.map((t) => (
              <div key={t.training_id} data-testid={`dash-today-${t.training_id}`} className="px-5 py-3.5 flex items-start gap-3">
                <div className="text-sm font-mono font-semibold text-zinc-900 w-14 shrink-0 pt-0.5">
                  {extractTime(t.date) || "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{t.title || t.group_name || "Allenamento"}</span>
                    <IntensityBadge intensity={t.intensity} />
                    <StatusPill status={t.status} />
                  </div>
                  <div className="text-xs text-zinc-500 font-mono mt-0.5">
                    {t.group_name || "—"} · {t.duration || 90}′
                    {t.coach_name ? ` · ${t.coach_name}` : ""}
                    {typeof t.expected_count === "number" ? ` · ${t.expected_count} attesi` : ""}
                  </div>
                  {t.objective && <div className="text-xs text-zinc-600 mt-0.5 truncate">🎯 {t.objective}</div>}
                  {(t.weapons || []).length > 0 && (
                    <div className="mt-1"><WeaponSummary weapons={(t.weapons || []).map((w) => ({ weapon_code: w, is_primary: true, status: "attiva" }))} /></div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Link to={`/allenamento/${t.training_id}`} data-testid={`dash-today-open-${t.training_id}`}
                      className="text-xs uppercase tracking-wider text-zinc-700 border border-zinc-200 rounded px-2.5 py-1 hover:bg-zinc-50 transition-colors">
                      Apri allenamento
                    </Link>
                    <button onClick={() => openRegister(t.training_id)} data-testid={`dash-today-att-${t.training_id}`}
                      className="text-xs uppercase tracking-wider text-white bg-[#0052FF] rounded px-2.5 py-1 hover:bg-blue-700 transition-colors">
                      Registra presenze
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {todoToday > 0 && (
          <div className="px-5 py-2 border-t border-zinc-100 text-xs text-zinc-500">
            {todoToday} session{todoToday > 1 ? "i" : "e"} ancora da registrare oggi.
          </div>
        )}
      </CardShell>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* 2 · QUESTA SETTIMANA */}
        <CardShell title="Questa settimana" icon={CalendarDays} iconClass="text-zinc-700" to="/allenamento" testid="dash-week">
          <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-zinc-100">
            <div><div className="text-2xl font-heading font-bold" data-testid="week-count">{week.count || 0}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Sessioni</div></div>
            <div><div className="text-2xl font-heading font-bold text-emerald-600" data-testid="week-done">{week.done || 0}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Svolte</div></div>
            <div><div className="text-2xl font-heading font-bold text-[#0052FF]" data-testid="week-upcoming">{week.upcoming || 0}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Da fare</div></div>
          </div>
          {(week.group_names || []).length > 0 && (
            <div className="px-5 py-2 border-b border-zinc-100 flex flex-wrap gap-1.5">
              {(week.group_names || []).map((n) => (
                <span key={n} className="text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-600 rounded px-2 py-0.5">{n}</span>
              ))}
            </div>
          )}
          {(week.next_sessions || []).length > 0 ? (
            <div className="divide-y divide-zinc-100">
              {week.next_sessions.map((s) => (
                <Link key={s.id} to={`/allenamento/${s.id}`} data-testid={`week-next-${s.id}`}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-zinc-50 transition-colors">
                  <Clock className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{s.title}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{fmtDate(s.date)} {extractTime(s.date)} · {(s.group_names || []).join(", ") || "—"}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-300" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="px-5 py-6 text-center text-sm text-zinc-400" data-testid="week-empty">Nessuna sessione futura questa settimana.</div>
          )}
        </CardShell>

        {/* 3 · PROSSIME GARE */}
        <CardShell title="Prossime gare" icon={Trophy} iconClass="text-amber-500" to="/gare" testid="dash-comps">
          {upcoming.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-500" data-testid="dash-comps-empty">
              Nessuna gara futura inserita nel calendario.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {upcoming.map((c, i) => {
                const dd = daysUntil(c.date);
                const nearest = i === 0;
                return (
                  <Link key={c.id} to="/gare" data-testid={`dash-comp-${c.id}`}
                    className={`flex items-center gap-3 px-5 py-3.5 hover:bg-zinc-50 transition-colors ${nearest ? "bg-amber-50/40" : ""}`}>
                    <div className={`flex flex-col items-center justify-center w-12 h-12 rounded shrink-0 ${nearest ? "bg-amber-500 text-white" : "bg-zinc-100 text-zinc-700"}`}>
                      <span className="text-base font-bold leading-none">{dd != null ? (dd === 0 ? "oggi" : dd) : "—"}</span>
                      {dd != null && dd !== 0 && <span className="text-[9px] uppercase tracking-wider">giorni</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`truncate ${nearest ? "font-semibold" : "font-medium"}`}>{c.name}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-0.5 flex items-center gap-1 flex-wrap">
                        <span>{fmtDate(c.date)}</span>
                        {c.location && <><span>·</span><MapPin className="w-3 h-3" />{c.location}</>}
                        {c.weapon && <><span>·</span>{c.weapon}</>}
                        {c.category && <><span>·</span>{c.category}</>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-300" />
                  </Link>
                );
              })}
            </div>
          )}
        </CardShell>

        {/* 4 · ATTENZIONE / PRESENZE */}
        <CardShell title="Attenzione & presenze" icon={AlertTriangle} iconClass="text-red-500" to="/presenze" testid="dash-attention">
          {attention.length === 0 && alerts.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-zinc-500" data-testid="dash-attention-empty">
              Nessuna criticità nelle presenze rilevata.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {alerts.map((a) => (
                <div key={a.athlete_id} data-testid={`dash-alert-${a.athlete_id}`} className="flex items-center gap-3 px-5 py-3">
                  <Activity className="w-4 h-4 text-red-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{a.name || a.athlete_id}</div>
                    <div className="text-xs text-zinc-500">{a.absences} assenze negli ultimi 30 giorni</div>
                  </div>
                  <Link to={`/atleti/${a.athlete_id}`} className="text-[#0052FF]"><ChevronRight className="w-4 h-4" /></Link>
                </div>
              ))}
              {attention.map((a) => (
                <Link key={a.id} to={`/atleti/${a.id}`} data-testid={`dash-attention-${a.id}`}
                  className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{displayName(a)}</div>
                    <div className="text-xs text-zinc-500 truncate">{a.reason}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
                    {a.status || "attenzione"}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </CardShell>

        {/* 5 · GRUPPI */}
        <CardShell title="Gruppi" icon={Users2} iconClass="text-zinc-700" to="/gruppi" testid="dash-groups">
          <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-zinc-100">
            <div><div className="text-2xl font-heading font-bold" data-testid="groups-total">{go.total_athletes || 0}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Atleti</div></div>
            <div><div className="text-2xl font-heading font-bold text-emerald-600" data-testid="groups-with">{go.with_group || 0}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Con gruppo</div></div>
            <Link to="/gruppi" data-testid="groups-without" className="group">
              <div className="text-2xl font-heading font-bold text-amber-600 group-hover:underline">{go.without_group || 0}</div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 group-hover:text-amber-600">Senza gruppo</div>
            </Link>
          </div>
          <div className="divide-y divide-zinc-100 max-h-64 overflow-y-auto">
            {(go.groups || []).filter((g) => g.count > 0).slice(0, 8).map((g) => (
              <Link key={g.id} to="/gruppi" data-testid={`dash-group-${g.id}`}
                className="flex items-center gap-3 px-5 py-2.5 hover:bg-zinc-50 transition-colors">
                <span className="text-sm font-medium flex-1 truncate">{g.name}</span>
                <span className="text-xs font-mono text-zinc-500">{g.count} atleti</span>
                <ChevronRight className="w-4 h-4 text-zinc-300" />
              </Link>
            ))}
            {(go.groups || []).filter((g) => g.count > 0).length === 0 && (
              <div className="px-5 py-6 text-center text-sm text-zinc-400">Nessun gruppo popolato.</div>
            )}
          </div>
        </CardShell>

        {/* 6 · TEST */}
        <CardShell title="Test stagione" icon={ClipboardList} iconClass="text-violet-600" to="/test" testid="dash-tests">
          {(tests.total_results || 0) > 0 ? (
            <div className="px-5 py-4 grid grid-cols-3 gap-3">
              <div><div className="text-2xl font-heading font-bold" data-testid="tests-results">{tests.total_results}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Risultati</div></div>
              <div><div className="text-2xl font-heading font-bold">{tests.athletes_tested || 0}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Atleti testati</div></div>
              <div><div className="text-sm font-heading font-bold pt-1.5" data-testid="tests-last">{tests.last_date ? fmtDate(tests.last_date) : "—"}</div><div className="text-[11px] uppercase tracking-wider text-zinc-500">Ultimo test</div></div>
            </div>
          ) : (
            <div className="px-5 py-8 text-center text-sm text-zinc-500" data-testid="dash-tests-empty">
              Nessun risultato test registrato. <Link to="/test" className="text-[#0052FF] underline">Vai a CSP Lab</Link>
            </div>
          )}
        </CardShell>

        {/* 7 · AZIONI RAPIDE */}
        <CardShell title="Azioni rapide" icon={Zap} iconClass="text-[#0052FF]" testid="dash-quick">
          <div className="p-4 grid grid-cols-2 gap-2">
            {[
              { to: "/allenamento", label: "Nuovo allenamento", icon: Dumbbell, tid: "qa-new-training" },
              { to: "/presenze", label: "Registra presenze", icon: CalendarCheck, tid: "qa-attendance" },
              { to: "/gruppi", label: "Gestisci gruppi", icon: Users2, tid: "qa-groups" },
              { to: "/allenamento", label: "Importa / Genera", icon: Flame, tid: "qa-import" },
              { to: "/gare", label: "Gare", icon: Trophy, tid: "qa-comps" },
              { to: "/test", label: "Test", icon: ClipboardList, tid: "qa-tests" },
            ].map((a) => (
              <Link key={a.tid} to={a.to} data-testid={a.tid}
                className="flex items-center gap-2 px-3 py-3 border border-zinc-200 rounded-md hover:border-[#0052FF] hover:bg-blue-50/40 transition-colors">
                <a.icon className="w-4 h-4 text-zinc-500 shrink-0" />
                <span className="text-sm font-medium">{a.label}</span>
              </Link>
            ))}
          </div>
        </CardShell>
      </div>

      {editRegisterId && (
        <AttendanceRegisterEditor
          registerId={editRegisterId}
          siblings={siblings}
          onNavigate={(t) => openRegister(t.training_id)}
          onClose={() => { setEditRegisterId(null); load(); }}
          onSaved={load}
        />
      )}
    </div>
  );
}
